/**
 * Чтение контекста виджета, приходящего в iframe.
 *
 * Контекст поступает ДВУМЯ путями (поддерживаем оба):
 *   (1) query-параметры URL iframe (навигационные: dealId, lang, theme, subdomain, accountId, userId);
 *   (2) postMessage от родительского script.js виджета (тип PAY_WIDGET_INIT) — сюда же приходит
 *       ДОВЕРЕННЫЙ подписанный контекст amoCRM (signedContext) и, возможно, apiBase/jwt.
 *
 * Протокол postMessage (единый на весь проект):
 *   iframe → parent:  { type: 'PAY_WIDGET_READY' }
 *   parent → iframe:  { type: 'PAY_WIDGET_INIT', payload: { ... } }
 *
 * Origin родителя строго проверяется по списку доверенных доменов amoCRM/Kommo.
 * READY постим ТОЛЬКО на известный доверенный origin (никогда на '*').
 *
 * Источник истины account_id/user_id для сервера — signedContext (его подпись сервер проверяет),
 * а НЕ query-параметры. dealId — всегда только навигационный, перепроверяется на сервере по тенанту.
 */

import type { SignedContext } from './types';

/** Единый список доверенных суффиксов доменов родителя (CSP/CORS/origin-чеки). */
export const TRUSTED_PARENT_SUFFIXES = ['.amocrm.ru', '.amocrm.com', '.kommo.com'] as const;

export type WidgetTheme = 'day' | 'night';

/** Полезная нагрузка PAY_WIDGET_INIT от родителя. Поля навигационные, кроме signedContext. */
export interface WidgetInitPayload {
  accountId?: string;
  dealId?: string | null;
  userId?: string;
  amoUserId?: string;
  subdomain?: string;
  /** Доверенный подписанный контекст amoCRM (сервер верифицирует подпись). */
  signedContext?: SignedContext | null;
  /** Язык интерфейса amoCRM (ru|en|…). */
  lang?: string;
  locale?: string;
  theme?: WidgetTheme;
  /** Некоторые сборки script.js передают apiBase/jwt — фронт их не обязан использовать. */
  apiBase?: string;
  jwt?: string;
}

/** Нормализованный контекст виджета, используемый приложением. */
export interface WidgetContext {
  /** amoCRM deal id (навигационный), строкой. Может отсутствовать вне карточки сделки. */
  dealId: string | null;
  /** amoCRM account id (навигационный), строкой. */
  accountId: string | null;
  /** amoCRM user id (навигационный), строкой. */
  userId: string | null;
  /** Субдомен аккаунта. */
  subdomain: string | null;
  /** Доверенный подписанный контекст (для POST /api/auth/embed). */
  signedContext: SignedContext | null;
  /** Язык (ru по умолчанию). */
  lang: string;
  /** Тема оформления. */
  theme: WidgetTheme;
  /** Origin родителя (для последующих postMessage, если понадобится). */
  parentOrigin: string | null;
}

export function isTrustedParentOrigin(origin: string): boolean {
  try {
    const u = new URL(origin);
    if (u.protocol !== 'https:') return false;
    return TRUSTED_PARENT_SUFFIXES.some((suf) => u.hostname === suf.slice(1) || u.hostname.endsWith(suf));
  } catch {
    return false;
  }
}

/** Прочитать навигационный контекст из query-параметров URL текущего iframe. */
export function readContextFromUrl(search: string = window.location.search): Partial<WidgetContext> {
  const p = new URLSearchParams(search);
  const theme = p.get('theme');
  return {
    dealId: p.get('dealId'),
    accountId: p.get('accountId'),
    userId: p.get('userId'),
    subdomain: p.get('subdomain'),
    lang: p.get('lang') ?? p.get('locale') ?? 'ru',
    theme: theme === 'night' ? 'night' : 'day',
  };
}

/** Определить доверенный origin родителя из document.referrer (в iframe это origin родителя). */
export function resolveParentOrigin(): string | null {
  try {
    if (document.referrer) {
      const origin = new URL(document.referrer).origin;
      if (isTrustedParentOrigin(origin)) return origin;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function normalizePayload(
  payload: WidgetInitPayload,
  base: Partial<WidgetContext>,
  parentOrigin: string | null,
): WidgetContext {
  const theme: WidgetTheme = payload.theme ?? base.theme ?? 'day';
  return {
    dealId: payload.dealId ?? base.dealId ?? null,
    accountId: payload.accountId ?? base.accountId ?? null,
    userId: payload.userId ?? payload.amoUserId ?? base.userId ?? null,
    subdomain: payload.subdomain ?? base.subdomain ?? null,
    signedContext: payload.signedContext ?? null,
    lang: payload.lang ?? payload.locale ?? base.lang ?? 'ru',
    theme,
    parentOrigin,
  };
}

export interface AcquireContextResult {
  /** Отписаться от message-слушателя. */
  dispose: () => void;
}

/**
 * Слушать PAY_WIDGET_INIT и вызвать onReady c нормализованным контекстом (однократно).
 * Сразу же шлёт PAY_WIDGET_READY родителю (если известен доверенный origin), чтобы parent
 * прислал INIT. Идемпотентно: повторные INIT игнорируются.
 *
 * Если parent прислал INIT без signedContext (например legacy-сборка), контекст всё равно
 * доставляется — решение о валидности примет сервер на /auth/embed (вернёт 401 при отсутствии подписи).
 */
/**
 * Сколько ждать PAY_WIDGET_INIT от родителя перед фолбэком на URL-контекст.
 * INIT (если придёт) применяется мгновенно; таймер срабатывает ТОЛЬКО когда handshake не дошёл.
 */
const INIT_FALLBACK_MS = 500;

export function acquireWidgetContext(onReady: (ctx: WidgetContext) => void): AcquireContextResult {
  const urlBase = readContextFromUrl();
  const parentOrigin = resolveParentOrigin();
  let done = false;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  const clearFallback = (): void => {
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  /** Ключ «на какой сделке мы сейчас» — чтобы отличить повторный INIT от смены сделки. */
  let currentDealId: string | null = null;

  const finish = (ctx: WidgetContext): void => {
    if (done) return;
    done = true;
    currentDealId = ctx.dealId;
    clearFallback();
    onReady(ctx);
  };

  function handleMessage(ev: MessageEvent): void {
    if (!isTrustedParentOrigin(ev.origin)) return;
    const data = ev.data as { type?: string; payload?: WidgetInitPayload } | null;
    if (!data || data.type !== 'PAY_WIDGET_INIT') return;
    const ctx = normalizePayload(data.payload ?? {}, urlBase, ev.origin);
    if (!done) {
      finish(ctx);
      return;
    }
    // Переход на ДРУГУЮ сделку в amoCRM: iframe переиспользуется (его перезагрузка = полный
    // рестарт приложения), поэтому родитель шлёт новый INIT. Отдаём новый контекст наверх —
    // useAuth переавторизуется под новую сделку, данные перезапросятся.
    if (ctx.dealId && ctx.dealId !== currentDealId) {
      currentDealId = ctx.dealId;
      onReady(ctx);
    }
  }

  window.addEventListener('message', handleMessage);

  // Сообщаем родителю о готовности принять INIT. Только на доверенный origin.
  if (parentOrigin) {
    try {
      window.parent.postMessage({ type: 'PAY_WIDGET_READY' }, parentOrigin);
    } catch {
      /* ignore */
    }
  }

  // Фолбэк «раз и навсегда»: если родитель не прислал PAY_WIDGET_INIT (гонка READY→INIT,
  // сбой/зависание getSignedContext в script.js, несовпадение origin) — работаем по
  // навигационному контексту из URL. script.js ВСЕГДА кладёт dealId/accountId/userId/subdomain
  // в src iframe, а SDK amoCRM signedContext не выдаёт, поэтому URL-контекст функционально
  // эквивалентен INIT; сервер в любом случае перепроверяет аккаунт/пользователя.
  // Итог: виджет НИКОГДА не зависает на «Подключение к amoCRM…».
  if (urlBase.userId) {
    fallbackTimer = setTimeout(() => {
      finish(normalizePayload({}, urlBase, parentOrigin));
    }, INIT_FALLBACK_MS);
  }

  return {
    dispose: () => {
      window.removeEventListener('message', handleMessage);
      clearFallback();
    },
  };
}
